import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Home, Settings, CreditCard, LogOut, Menu, X, Check, Sparkles, Crown, Users, Loader2, Copy, CheckCircle2, ArrowLeft, QrCode, ExternalLink, Star, Phone } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { HelpButton } from '@/components/HelpButton';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_a72ee028-442b-44ea-ad3d-be1abd62a5cf/artifacts/zdjtc3rc_Logo_Falo_com_o_dono-removebg-preview.png";

const Plans = () => {
  const { user, getToken, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const promoCoupon = (searchParams.get('promo') || searchParams.get('cupom') || '').trim().toUpperCase();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState('plans');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [cpfCnpj, setCpfCnpj] = useState('');
  const [billingType, setBillingType] = useState('UNDEFINED');
  const [subscribing, setSubscribing] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [copied, setCopied] = useState(false);
  const pollRef = useRef(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [changingPlan, setChangingPlan] = useState(null);

  useEffect(() => { if (!authLoading && !user && !localStorage.getItem('token')) navigate('/login'); }, [user, authLoading, navigate]);
  useEffect(() => { fetchPlans(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, []);

  // Auto-seleciona plano via query param (?plan=basico) — usado pelo banner de trial expirado no Dashboard
  useEffect(() => {
    const planParam = (searchParams.get('plan') || '').trim().toLowerCase();
    if (planParam && plans.length > 0 && checkoutStep === 'plans') {
      const found = plans.find(p => p.id === planParam);
      if (found) {
        setSelectedPlan(found);
        setCheckoutStep('form');
      }
    }
  }, [searchParams, plans]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPlans = useCallback(async () => {
    try {
      const planParam = (searchParams.get('plan') || '').trim().toLowerCase();
      const res = await axios.get(`${API}/plans${planParam ? `?include=${planParam}` : ''}`);
      setPlans(res.data);
    } catch { toast.error('Erro ao carregar planos'); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPaymentStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/payments/status`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setPaymentStatus(res.data);
    } catch (e) { console.error('Erro ao verificar status do pagamento:', e); }
  }, [getToken]);

  useEffect(() => { if (user) fetchPaymentStatus(); }, [user, fetchPaymentStatus]);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await axios.post(`${API}/payments/cancel`, {}, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success('Assinatura cancelada com sucesso');
      setShowCancelConfirm(false);
      fetchPaymentStatus();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao cancelar'); }
    finally { setCancelling(false); }
  };

  const handleChangePlan = async (planId) => {
    setChangingPlan(planId);
    try {
      const res = await axios.post(`${API}/payments/change-plan`, { plan_id: planId }, { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success(res.data.message);
      fetchPaymentStatus();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao alterar plano'); }
    finally { setChangingPlan(null); }
  };

  const formatCpfCnpj = (v) => {
    const d = v.replace(/\D/g, '');
    if (d.length <= 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  };

  const handleSubscribe = async () => {
    const digits = cpfCnpj.replace(/\D/g, '');
    if (digits.length !== 11 && digits.length !== 14) { toast.error('CPF ou CNPJ inválido'); return; }

    setSubscribing(true);
    try {
      const payload = {
        plan_id: selectedPlan.id,
        cpf_cnpj: digits,
        name: user?.name || 'Cliente',
        email: user?.email || '',
        billing_type: billingType
      };
      if (promoCoupon) payload.coupon_code = promoCoupon;

      const res = await axios.post(`${API}/payments/subscribe`, payload, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });

      setPaymentData(res.data);
      setCheckoutStep('payment');

      if (res.data.coupon_code && res.data.discount_amount > 0) {
        toast.success(`Cupom ${res.data.coupon_code} aplicado! Desconto de R$ ${res.data.discount_amount.toFixed(2).replace('.', ',')}`);
      }

      if (res.data.payment_id) {
        pollRef.current = setInterval(async () => {
          try {
            const st = await axios.get(`${API}/payments/pix-status/${res.data.payment_id}`, {
              headers: { Authorization: `Bearer ${getToken()}` }
            });
            if (st.data.confirmed) {
              clearInterval(pollRef.current);
              setCheckoutStep('success');
              toast.success('Pagamento confirmado!');
            }
          } catch (err) { console.warn('Polling: aguardando confirmacao...', err.message); }
        }, 5000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar pagamento');
    } finally { setSubscribing(false); }
  };

  const copyPix = () => {
    if (paymentData?.pix_payload) {
      navigator.clipboard.writeText(paymentData.pix_payload);
      setCopied(true);
      toast.success('Código PIX copiado!');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  if (authLoading || loading) return <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]"><div className="spinner" /></div>;
  if (!user) return null;

  const Sidebar = ({ mobile = false }) => (
    <div className={`flex flex-col h-full ${mobile ? '' : 'fixed left-0 top-0 h-screen w-64'}`}>
      <div className="p-5 border-b border-white/10">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Logo" className="h-20 w-auto object-contain" />
          <div><span className="font-semibold text-white text-sm">Falo Com O Dono</span><p className="text-xs text-slate-400">Painel do Dono</p></div>
        </div>
      </div>
      <nav className="flex-1 p-4 space-y-1">
        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider px-4 mb-3">Menu</p>
        <Link to="/dashboard" className="sidebar-link"><Home className="w-5 h-5" />Painel do Dono</Link>
        <Link to="/settings" className="sidebar-link"><Settings className="w-5 h-5" />Configurações</Link>
        <Link to="/plans" className="sidebar-link sidebar-link-active"><CreditCard className="w-5 h-5" />Gerenciar Plano</Link>
      </nav>
      <div className="p-4 border-t border-white/10">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"><Users className="w-5 h-5 text-white" /></div>
          <div className="flex-1 min-w-0"><p className="text-sm font-medium text-white truncate">{user?.name}</p><p className="text-xs text-slate-400 truncate">{user?.company_name}</p></div>
        </div>
        <Button variant="ghost" className="w-full justify-start text-slate-400 hover:text-white hover:bg-white/10 text-sm" onClick={handleLogout}><LogOut className="w-4 h-4 mr-2" />Sair</Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="plans-page">
      <div className="lg:hidden bg-[#0A2647] text-white h-16 flex items-center justify-between px-4 sticky top-0 z-40">
        <div className="flex items-center gap-3"><img src={LOGO_URL} alt="Logo" className="h-16 w-auto object-contain" /><span className="font-semibold text-sm">Falo Com O Dono</span></div>
        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)} className="text-white hover:bg-white/10">{sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}</Button>
      </div>
      {sidebarOpen && <div className="lg:hidden fixed inset-0 z-50"><div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} /><div className="absolute left-0 top-0 h-full w-64 bg-[#0A2647]"><Sidebar mobile /></div></div>}
      <div className="hidden lg:block w-64 bg-[#0A2647] fixed left-0 top-0 h-screen z-30"><Sidebar /></div>

      <div className="lg:ml-64 min-h-screen">
        <header className="bg-white border-b border-slate-200 px-6 py-4 sticky top-0 z-20">
          <h1 className="text-xl font-semibold text-slate-900">Planos e Pagamento</h1>
          <p className="text-sm text-slate-500">
            {checkoutStep === 'plans' && 'Escolha o plano ideal para o seu negócio'}
            {checkoutStep === 'form' && `Finalizando: ${selectedPlan?.name}`}
            {checkoutStep === 'payment' && 'Realize o pagamento'}
            {checkoutStep === 'success' && 'Pagamento confirmado!'}
          </p>
        </header>

        <main className="p-6"><div className="max-w-6xl mx-auto">

          {/* PROMO BANNER (cupom via 1-clique) */}
          {checkoutStep === 'plans' && promoCoupon && (
            <div
              className="mb-6 rounded-xl border-2 border-dashed border-[#C5A059] bg-gradient-to-r from-[#FFF8E7] to-[#FFFBEF] p-5 flex items-center gap-4 shadow-sm"
              data-testid="promo-coupon-banner"
            >
              <div className="w-12 h-12 rounded-full bg-[#C5A059] flex items-center justify-center text-2xl flex-shrink-0">🎁</div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-[#C5A059] uppercase tracking-wider mb-1">Cupom Promocional Ativo</p>
                <p className="text-lg font-bold text-[#0A2647] truncate" data-testid="promo-coupon-code">
                  {promoCoupon}
                </p>
                <p className="text-sm text-slate-600 mt-0.5">
                  {promoCoupon === 'ULTIMA50' && 'Desconto de 50% no 1º mês. A partir do 2º mês, valor cheio.'}
                  {promoCoupon === 'VOLTA20' && 'Desconto de 20% no 1º mês. A partir do 2º mês, valor cheio.'}
                  {promoCoupon === 'BEMVINDO10' && 'Desconto de 10% no 1º mês. A partir do 2º mês, valor cheio.'}
                  {!['ULTIMA50','VOLTA20','BEMVINDO10'].includes(promoCoupon) && 'Cupom registrado em sua conta.'}
                </p>
                <p className="text-xs text-emerald-700 font-medium mt-1">✅ O desconto será aplicado automaticamente ao escolher um plano abaixo.</p>
              </div>
            </div>
          )}

          {/* MINHA ASSINATURA */}
          {checkoutStep === 'plans' && paymentStatus && paymentStatus.plan !== 'free' && (
            <Card className="mb-6 border-0 shadow-sm border-l-4 border-l-[#C5A059]" data-testid="my-subscription-card">
              <CardContent className="p-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">Minha Assinatura</p>
                    <p className="text-lg font-bold text-slate-900">
                      {plans.find(p => p.id === paymentStatus.plan)?.name || paymentStatus.plan}
                    </p>
                    <p className="text-sm text-slate-500 mt-0.5">
                      Status: <span className={paymentStatus.subscription_status === 'active' ? 'text-emerald-600 font-semibold' : 'text-amber-600 font-semibold'}>
                        {paymentStatus.subscription_status === 'active' ? 'Ativa' : paymentStatus.subscription_status === 'cancelled' ? 'Cancelada' : paymentStatus.subscription_status}
                      </span>
                    </p>
                    <p className="text-xs text-slate-400 mt-1">Pagamentos PIX: o Asaas envia automaticamente o novo código antes do vencimento.</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="border-red-300 text-red-600 hover:bg-red-50 text-xs h-9"
                      onClick={() => setShowCancelConfirm(true)}
                      data-testid="cancel-subscription-btn"
                    >
                      Cancelar Assinatura
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* STEP 1: Plans */}
          {checkoutStep === 'plans' && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 items-stretch" data-testid="plans-grid">
              {plans.map((plan) => {
                const isPopular = plan.id === 'enterprise';
                const isComplete = plan.id === 'five_stars';
                const isHighlighted = isPopular || isComplete;
                const isFree = plan.id === 'free';
                const isCurrentPlan = plan.id === (user?.plan || 'free');
                const hasWhatsapp = ['free', 'basico', 'pro', 'enterprise', 'elite', 'five_stars'].includes(plan.id);
                return (
                  <Card key={plan.id} className={`relative transition-all duration-300 flex flex-col border ${isHighlighted ? 'ring-2 ring-[#C5A059] shadow-lg' : 'border-slate-200 shadow-sm'}`} data-testid={`plan-card-${plan.id}`}>
                    {isPopular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10"><span className="bg-[#C5A059] text-[#0A2647] px-3 py-1 rounded-full text-[10px] font-bold inline-flex items-center gap-1 shadow-sm whitespace-nowrap"><Sparkles className="w-3 h-3" />Mais Popular</span></div>}
                    {isComplete && <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10"><span className="bg-[#0A2647] text-[#C5A059] px-3 py-1 rounded-full text-[10px] font-bold inline-flex items-center gap-1 shadow-sm whitespace-nowrap"><Crown className="w-3 h-3" />Mais Completo</span></div>}
                    <CardHeader className="text-center pt-6 pb-2">
                      <CardTitle className="text-sm font-semibold text-slate-900">{plan.name}</CardTitle>
                      {plan.seal && (
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-[#C5A059]">{plan.seal}</p>
                      )}
                      {hasWhatsapp && (
                        <div className="mt-1">
                          <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-emerald-500 text-white" data-testid={`whatsapp-badge-${plan.id}`}>
                            <Phone className="w-2.5 h-2.5" />Alertas WhatsApp
                          </span>
                        </div>
                      )}
                      {!isFree && !hasWhatsapp && (
                        <div className="mt-1">
                          <span className="inline-block text-[9px] font-bold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-emerald-500 text-white">Assine Agora</span>
                        </div>
                      )}
                      <div className="mt-2">
                        {isFree ? (
                          <span className="text-2xl font-bold text-slate-900">Grátis</span>
                        ) : (
                          <><span className="text-2xl font-bold text-slate-900">R$ {plan.price.toFixed(2).replace('.', ',')}</span><span className="text-slate-500 text-xs">/mês</span></>
                        )}
                      </div>
                      {plan.upgrade && (
                        <p className="text-[11px] text-slate-400 mt-2 pb-2 border-b border-slate-100">{plan.upgrade}</p>
                      )}
                    </CardHeader>
                    <CardContent className="pt-0 px-4 flex-1 flex flex-col">
                      <ul className="space-y-2 mb-4 flex-1">
                        {plan.features.map((f) => {
                          const isGoogleFeature = f.includes('Alavanque sua Nota no Google') || f.includes('Alavanque sua Nota');
                          return (
                            <li key={f} className="flex items-start gap-2 text-xs text-slate-600">
                              <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${isGoogleFeature ? 'bg-[#C5A059]/20' : 'bg-emerald-50'}`}>
                                {isGoogleFeature ? <Star className="w-2.5 h-2.5 text-[#C5A059]" /> : <Check className="w-2.5 h-2.5 text-emerald-600" />}
                              </div>
                              <span className={isGoogleFeature ? 'font-bold text-[#0A2647]' : ''}>{f}</span>
                            </li>
                          );
                        })}
                        {hasWhatsapp && (
                          <li className="flex items-start gap-2 text-xs" data-testid={`wa-feature-${plan.id}`}>
                            <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-emerald-500">
                              <Phone className="w-2.5 h-2.5 text-white" />
                            </div>
                            <span className="font-bold text-emerald-700">Alerta de Crise + Resumo Semanal via WhatsApp</span>
                          </li>
                        )}
                      </ul>
                      {isCurrentPlan ? (
                        <Button disabled className="w-full bg-slate-100 text-slate-500 cursor-default text-xs h-9" data-testid={`select-plan-${plan.id}`}>
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" />Plano Atual
                        </Button>
                      ) : isFree ? (
                        <Button disabled className="w-full bg-slate-100 text-slate-400 cursor-default text-xs h-9" data-testid={`select-plan-${plan.id}`}>
                          Plano Free
                        </Button>
                      ) : paymentStatus?.asaas_subscription_id && paymentStatus?.plan !== 'free' ? (
                        <Button
                          onClick={() => handleChangePlan(plan.id)}
                          disabled={changingPlan === plan.id}
                          className={`w-full text-xs h-9 ${isHighlighted ? 'btn-secondary' : 'btn-primary'}`}
                          data-testid={`change-plan-${plan.id}`}
                        >
                          {changingPlan === plan.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
                          Mudar para este Plano
                        </Button>
                      ) : (
                        <Button onClick={() => { setSelectedPlan(plan); setCheckoutStep('form'); }} className={`w-full text-xs h-9 ${isHighlighted ? 'btn-secondary' : 'btn-primary'}`} data-testid={`select-plan-${plan.id}`}>
                          Assinar Plano
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* STEP 2: Form with payment method */}
          {checkoutStep === 'form' && (
            <div className="max-w-md mx-auto" data-testid="checkout-form">
              <Card className="card-premium">
                <CardHeader className="bg-gradient-to-r from-[#0A2647] to-[#0D3158] text-white rounded-t-xl">
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-6 h-6 text-[#C5A059]" />
                    <div><CardTitle className="text-base">{selectedPlan?.name}</CardTitle><CardDescription className="text-slate-300">R$ {selectedPlan?.price.toFixed(2).replace('.', ',')}/mes — Cobrança imediata</CardDescription></div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6 space-y-5">
                  <div>
                    <Label className="text-sm font-medium text-slate-700">CPF ou CNPJ</Label>
                    <Input type="text" placeholder="000.000.000-00" value={cpfCnpj} onChange={(e) => setCpfCnpj(formatCpfCnpj(e.target.value))} maxLength={18} className="mt-1.5" data-testid="cpf-cnpj-input" />
                  </div>

                  <div>
                    <Label className="text-sm font-medium text-slate-700 mb-2 block">Forma de Pagamento</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { id: 'UNDEFINED', label: 'PIX', icon: '◉', desc: 'Instantâneo' },
                        { id: 'CREDIT_CARD', label: 'Cartão', icon: '◻', desc: 'Crédito' },
                        { id: 'BOLETO', label: 'Boleto', icon: '▥', desc: 'Bancário' },
                      ].map(opt => (
                        <button key={opt.id} onClick={() => setBillingType(opt.id)}
                          className={`p-3 rounded-lg border-2 text-center transition-all ${billingType === opt.id ? 'border-[#C5A059] bg-[#C5A059]/5' : 'border-slate-200 hover:border-slate-300'}`}
                          data-testid={`billing-${opt.id.toLowerCase()}`}>
                          <span className="text-lg block">{opt.icon}</span>
                          <span className="text-xs font-semibold block text-slate-800">{opt.label}</span>
                          <span className="text-[10px] text-slate-500">{opt.desc}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <Button variant="outline" onClick={() => { setCheckoutStep('plans'); setBillingType('UNDEFINED'); }} className="flex-1" data-testid="btn-back-plans"><ArrowLeft className="w-4 h-4 mr-2" />Voltar</Button>
                    <Button onClick={handleSubscribe} disabled={subscribing || cpfCnpj.replace(/\D/g, '').length < 11} className="flex-1 btn-secondary" data-testid="btn-confirm-subscribe">
                      {subscribing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processando...</> : 'Confirmar'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* STEP 3: Payment */}
          {checkoutStep === 'payment' && paymentData && (
            <div className="max-w-lg mx-auto space-y-4" data-testid="payment-step">
              <Card className="card-premium overflow-hidden">
                <CardHeader className="bg-gradient-to-r from-[#0A2647] to-[#0D3158] text-white text-center">
                  <QrCode className="w-8 h-8 text-[#C5A059] mx-auto mb-1" />
                  <CardTitle className="text-lg">Pagamento - {paymentData.plan_name}</CardTitle>
                  <CardDescription className="text-slate-300">R$ {paymentData.value?.toFixed(2).replace('.', ',')}/mes</CardDescription>
                </CardHeader>
                <CardContent className="pt-6 space-y-5">

                  {/* Asaas Checkout Link (Cartao + Boleto + PIX) */}
                  {paymentData.invoice_url && (
                    <a href={paymentData.invoice_url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-[#0A2647] hover:bg-[#0D3158] text-white rounded-lg font-semibold transition-colors"
                      data-testid="asaas-checkout-link">
                      <ExternalLink className="w-5 h-5" />
                      Pagar com Cartão, PIX ou Boleto
                    </a>
                  )}

                  {/* Inline PIX */}
                  {paymentData.pix_qrcode_image && (
                    <div className="border-t border-slate-200 pt-5">
                      <p className="text-sm font-medium text-slate-700 text-center mb-3">Ou pague direto com PIX:</p>
                      <div className="flex justify-center">
                        <div className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm">
                          <img src={`data:image/png;base64,${paymentData.pix_qrcode_image}`} alt="QR Code PIX" className="w-48 h-48" data-testid="pix-qrcode-img" />
                        </div>
                      </div>
                    </div>
                  )}

                  {paymentData.pix_payload && (
                    <div>
                      <Label className="text-sm font-medium text-slate-700 mb-1.5 block">Código PIX Copia e Cola:</Label>
                      <div className="flex gap-2">
                        <Input value={paymentData.pix_payload} readOnly className="text-xs font-mono bg-slate-50" data-testid="pix-payload-input" />
                        <Button onClick={copyPix} variant="outline" className="shrink-0" data-testid="btn-copy-pix">
                          {copied ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-center gap-2 text-sm text-amber-600 bg-amber-50 py-3 px-4 rounded-lg" data-testid="payment-waiting">
                    <Loader2 className="w-4 h-4 animate-spin" />Aguardando confirmação...
                  </div>
                  <p className="text-xs text-slate-500 text-center">Após o pagamento, seu plano será ativado automaticamente.</p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* STEP 4: Success */}
          {checkoutStep === 'success' && (
            <div className="max-w-md mx-auto text-center" data-testid="payment-success">
              <Card className="card-premium overflow-hidden">
                <CardContent className="pt-10 pb-8 space-y-4">
                  <div className="w-16 h-16 bg-emerald-50 rounded-full flex items-center justify-center mx-auto"><CheckCircle2 className="w-8 h-8 text-emerald-600" /></div>
                  <h2 className="text-xl font-bold text-slate-900">Pagamento Confirmado!</h2>
                  <p className="text-sm text-slate-600">Seu plano <strong>{paymentData?.plan_name}</strong> foi ativado.</p>
                  <Button onClick={() => navigate('/dashboard')} className="btn-secondary mt-4" data-testid="btn-go-dashboard">Ir para o Dashboard</Button>
                </CardContent>
              </Card>
            </div>
          )}

        </div></main>
      </div>

      {/* Cancel Confirmation Dialog */}
      {showCancelConfirm && (
        <Dialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-lg">Cancelar Assinatura</DialogTitle>
              <DialogDescription>
                Tem certeza que deseja cancelar? Seu plano será rebaixado para <strong>Free</strong> (máximo 10 avaliações/mês). Esta ação não pode ser desfeita.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2 sm:justify-end">
              <Button variant="outline" onClick={() => setShowCancelConfirm(false)} data-testid="cancel-dialog-no">
                Manter Plano
              </Button>
              <Button
                variant="destructive"
                onClick={handleCancel}
                disabled={cancelling}
                data-testid="cancel-dialog-confirm"
              >
                {cancelling ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                Sim, Cancelar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      <HelpButton ambiente="dono" tela="plans" />
    </div>
  );
};

export default Plans;
